import { Component } from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import { SubmissionLossReason, SUBMISSION_LOSS_REASONS } from '../../interfaces/submission';

/**
 * Tiny modal shown when a submission is moved to the `lost` stage (by drag or
 * by the per-card status select). A loss reason is REQUIRED (doc §12 — the
 * server also enforces it); the free-text comment stays optional. The dialog
 * closes with:
 *   - `{ reason, note }` -> confirmed (reason always set, note may be null),
 *   - the literal CANCEL -> cancelled (caller should revert the move).
 */
export const LOSS_DIALOG_CANCEL = Symbol('loss-dialog-cancel');

export interface LossDialogConfirmed {
  reason: SubmissionLossReason | null;
  note: string | null;
}

export type LossDialogResult = LossDialogConfirmed | typeof LOSS_DIALOG_CANCEL;

@Component({
  selector: 'bst-loss-reason-dialog',
  templateUrl: './loss-reason-dialog.component.html',
  styleUrls: ['./loss-reason-dialog.component.scss'],
  standalone: false,
})
export class LossReasonDialogComponent {

  readonly lossReasons = SUBMISSION_LOSS_REASONS;

  // Selected reason; confirm stays disabled until one is picked (required).
  reason: SubmissionLossReason | null = null;

  // Optional free-text comment, available for any reason.
  note = '';

  constructor(private ref: NbDialogRef<LossReasonDialogComponent>) {}

  // Read the picked reason from the select's (selectedChange) — avoids a
  // two-way CVA binding that re-fires on init.
  onReasonChange(value: SubmissionLossReason | null): void {
    this.reason = value ?? null;
  }

  confirm(): void {
    if (!this.reason) { return; }
    this.ref.close({
      reason: this.reason,
      note: this.note.trim() || null,
    } as LossDialogConfirmed);
  }

  cancel(): void {
    this.ref.close(LOSS_DIALOG_CANCEL);
  }
}
